/**
 * Lesson path statuses: done → the first unfinished available lesson is current → locked.
 * A placement test may set a start lesson: unfinished lessons before it are `open`
 * (playable in any order), and the current lesson is the first unfinished one from it on.
 */

export type PathStatus = 'done' | 'current' | 'open' | 'locked';

export interface PathLesson {
  id: string;
  available: boolean;
}

export function pathStatuses(
  lessons: readonly PathLesson[],
  doneIds: ReadonlySet<string>,
  startId: string | null = null,
): Map<string, PathStatus> {
  const result = new Map<string, PathStatus>();
  const startIndex = startId ? Math.max(0, lessons.findIndex((l) => l.id === startId)) : 0;
  const unfinished = (l: PathLesson) => l.available && !doneIds.has(l.id);
  const fromStart = lessons.findIndex((l, i) => i >= startIndex && unfinished(l));
  // Everything from the start on is done: fall back to the first unfinished lesson anywhere.
  const currentIndex = fromStart >= 0 ? fromStart : lessons.findIndex(unfinished);
  lessons.forEach((lesson, i) => {
    if (doneIds.has(lesson.id)) result.set(lesson.id, 'done');
    else if (i === currentIndex) result.set(lesson.id, 'current');
    else if (i < startIndex && lesson.available) result.set(lesson.id, 'open');
    else result.set(lesson.id, 'locked');
  });
  return result;
}

/** Up to `size` consecutive lessons around the current one (for the desktop wave). */
export function windowAround<T>(items: readonly T[], currentIndex: number, size: number): T[] {
  if (items.length <= size) return [...items];
  const anchor = currentIndex < 0 ? 0 : currentIndex;
  const start = Math.min(Math.max(0, anchor - 2), items.length - size);
  return items.slice(start, start + size);
}
